import React, { useState, useEffect, useMemo, useRef } from 'react';
import ListWithDetails from 'components/Common/ListWithDetails';
import GenericBadge from 'components/Utils/GenericBadge';
import { api } from 'careand-redux/api/ApiThunks';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import DefaultButton from 'components/Utils/NewDefaultButton';
import DefaultTextField from 'components/Utils/DefaultTextField';
import PerformanceUtils from 'services/PerformanceUtils';
import Icon from 'components/Utils/NewIcon';
import ConfirmationDialogWrapper from 'components/Utils/ConfirmationDialog/ConfirmationDialogWrapper';
import HookDetails from '../HookDetails';
import './style.scss';
import { EventSchema, HookConfiguration } from '../../../generated/api';

type ListHook = HookConfiguration & { isCategory?: boolean; categoryName?: string };

const HooksManagement: React.FC = () => {
  const dispatch = useAppDispatch();
  const [hooks, setHooks] = useState<HookConfiguration[]>([]);
  const [eventSchemas, setEventSchemas] = useState<EventSchema[]>([]);
  const [filter, setFilter] = useState<string>('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isCreateMode, setIsCreateMode] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadFailed, setLoadFailed] = useState<boolean>(false);
  const [pendingSelection, setPendingSelection] = useState<ListHook | 'create' | null>(null);
  const detailsDirty = useRef<boolean>(false);

  useEffect(() => {
    loadData();
  }, [dispatch]);

  async function loadData(): Promise<void> {
    setLoading(true);
    setLoadFailed(false);
    try {
      const [hooksResponse, schemasResponse] = await Promise.all([
        dispatch(api.Hooks.getHooks({})),
        dispatch(api.Hooks.getEventSchemas()),
      ]);
      if (!hooksResponse || !schemasResponse) {
        setLoadFailed(true);
        return;
      }
      setHooks(hooksResponse);
      setEventSchemas(schemasResponse as EventSchema[]);
    } catch (error) {
      console.error('Failed to load hooks:', error);
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }

  function onAddNewHook(): void {
    if (detailsDirty.current) {
      setPendingSelection('create');
      return;
    }
    enterCreateMode();
  }

  function enterCreateMode(): void {
    detailsDirty.current = false;
    setIsCreateMode(true);
    setSelectedId(null);
  }

  function onHookResult(hook: HookConfiguration): void {
    if (hook) {
      detailsDirty.current = false;
      if (isCreateMode) {
        setHooks((prevState) => [hook, ...prevState]);
        setIsCreateMode(false);
        setSelectedId(hook.id!);
      } else {
        setHooks((prevState) => [...PerformanceUtils.editOrAdd(hook, prevState)]);
      }
    }
  }

  function onHookDelete(hookId: string): void {
    detailsDirty.current = false;
    setHooks((prevState) => prevState.filter((h) => h.id !== hookId));
    setSelectedId((current) => (current === hookId ? null : current));
  }

  function onHookCancel(): void {
    detailsDirty.current = false;
    if (isCreateMode) {
      setIsCreateMode(false);
    }
  }

  const getSchema = (eventType: string): EventSchema | undefined =>
    eventSchemas.find((schema) => schema.event_type === eventType);

  // Group hooks by category
  const groupedHooks = useMemo(() => {
    const query = filter.trim().toLowerCase();
    const filtered = query
      ? hooks.filter((hook) => {
          const schema = getSchema(hook.event_type);
          return (
            hook.name?.toLowerCase().includes(query) ||
            hook.event_type.toLowerCase().includes(query) ||
            schema?.name?.toLowerCase().includes(query) ||
            schema?.category?.toLowerCase().includes(query)
          );
        })
      : hooks;

    const groups: { [category: string]: HookConfiguration[] } = {};

    filtered.forEach((hook) => {
      const category = getSchema(hook.event_type)?.category || 'Other';

      if (!groups[category]) {
        groups[category] = [];
      }
      //@ts-ignore
      groups[category].push(hook);
    });

    Object.keys(groups).forEach((category) => {
      //@ts-ignore
      groups[category].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    });

    return groups;
  }, [hooks, filter, eventSchemas]);

  // Flatten grouped hooks for the list (with category markers)
  const filteredHooks = useMemo(() => {
    const flatList: ListHook[] = [];
    const sortedCategories = Object.keys(groupedHooks).sort();

    sortedCategories.forEach((category) => {
      flatList.push({
        id: `category-${category}`,
        name: category,
        event_type: '',
        enabled: false,
        actions: [],
        isCategory: true,
        categoryName: category,
      } as any);

      //@ts-ignore
      flatList.push(...groupedHooks[category]);
    });

    return flatList;
  }, [groupedHooks]);

  const renderHookBadge = (hook: ListHook) => {
    if (hook.isCategory) {
      return (
        <div key={hook.id} className="hook-category-header">
          <span className="hook-category-title">{hook.categoryName}</span>
        </div>
      );
    }

    const actionCount = hook.actions?.length || 0;
    return (
      <GenericBadge
        key={hook.id || 'new'}
        title={hook.name || 'New Hook'}
        info={
          <div>
            <span>
              {getSchema(hook.event_type)?.name || hook.event_type || 'Select event type'}
            </span>
            <br />
            <span className={`hook-status ${hook.enabled ? 'enabled' : 'disabled'}`}>
              {hook.enabled ? '✓ Enabled' : '✗ Disabled'} • {actionCount} action
              {actionCount === 1 ? '' : 's'}
            </span>
          </div>
        }
      />
    );
  };

  const renderHookDetails = (hook: HookConfiguration | null) => {
    if (hook && (hook as ListHook).isCategory) {
      return null;
    }

    return (
      <HookDetails
        key={isCreateMode ? 'create-mode' : hook?.id || 'no-selection'}
        hook={isCreateMode ? null : hook}
        eventSchemas={eventSchemas}
        onSave={onHookResult}
        onDelete={onHookDelete}
        onCancel={onHookCancel}
        onDirtyChange={(dirty) => {
          detailsDirty.current = dirty;
        }}
      />
    );
  };

  const handleHookClick = (hook: ListHook) => {
    if (hook.isCategory || !hook.id) {
      return;
    }
    if (!isCreateMode && hook.id === selectedId) {
      return;
    }
    if (detailsDirty.current) {
      setPendingSelection(hook);
      return;
    }
    applySelection(hook);
  };

  function applySelection(hook: ListHook): void {
    detailsDirty.current = false;
    setIsCreateMode(false);
    setSelectedId(hook.id!);
  }

  const renderEmptyState = () => (
    <div className="hooks-empty-state">
      <Icon src="webhook" width="40px" />
      <p className="hooks-empty-state-title">No hook selected</p>
      <p className="hooks-empty-state-text">
        Select a hook from the list to view or edit it, or press "Add Hook" to create a new one.
      </p>
    </div>
  );

  const renderErrorState = () => (
    <div className="hooks-empty-state">
      <Icon src="info-circle" width="40px" />
      <p className="hooks-empty-state-title">Could not load hooks</p>
      <p className="hooks-empty-state-text">
        Something went wrong while loading the hooks. The list below may be incomplete.
      </p>
      <DefaultButton name="Retry" icon="rotate-right" onClick={loadData} />
    </div>
  );

  const overrideDetails = isCreateMode
    ? renderHookDetails(null)
    : loadFailed
    ? renderErrorState()
    : !selectedId
    ? renderEmptyState()
    : undefined;

  return (
    <div className="hooks-management box full-width full-height">
      <ListWithDetails
        items={filteredHooks}
        idField="id"
        loading={loading}
        selectedItem={isCreateMode ? null : selectedId}
        onClick={handleHookClick}
        overrideDetails={overrideDetails}
        addButton={
          <div className="hooks-controls">
            <DefaultButton
              name="Add Hook"
              icon="plus"
              onClick={onAddNewHook}
              disabled={isCreateMode}
              style={{ display: 'block', margin: 'auto', marginBottom: '16px', width: '80%' }}
            />
            <div style={{ margin: '0 20px' }}>
              <DefaultTextField
                label="Filter hooks..."
                style={{ width: '100%' }}
                name="filter"
                value={filter}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setFilter(event.target.value)
                }
                placeholder="Search by name, event or category"
              />
            </div>
          </div>
        }
        renderBadge={renderHookBadge}
        renderDetails={renderHookDetails}
      />

      <ConfirmationDialogWrapper
        open={!!pendingSelection}
        title="Unsaved Changes"
        message="You have unsaved changes on this hook. Discard them?"
        onConfirm={() => {
          const pending = pendingSelection;
          setPendingSelection(null);
          if (pending === 'create') {
            enterCreateMode();
          } else if (pending) {
            applySelection(pending);
          }
        }}
        onCancel={() => setPendingSelection(null)}
      />
    </div>
  );
};

export default HooksManagement;
